
// packages/shared/rbac.ts
// Unified RBAC — source of truth for the entire monorepo
// Copy to Vite apps: apps/csr/src/utils/rbac.ts, apps/admin-ui/src/utils/rbac.ts
// ALL RIGHTS RESERVED
//
// UPDATED 2026-01-31: MERGED treasury_admin + reserve_manager → treasury; ADDED council
// UPDATED 2026-02-04: Granted contracts:view to council, compliance, cpa_auditor, board_director
// UPDATED 2026-02-07: Phase 1 — Added dashboard:view_asset_holdings to cpa_auditor
// UPDATED 2026-02-07: Phase 2 — Promoted admin-ui 12-role model to shared (3 new roles:
//   board_director, cpa_auditor, custodian); added segregation-of-duties,
//   external contractor model, ROLE_TYPE, and full permission set (72 perms).
//   Granted read-only CSR access to security_admin (incident investigation),
//   council and board_director (governance oversight): csr:view_queue, csr:view_audit.

// =============================================================================
// ROLES (12 total)
//
// Internal Employees:
//   admin, council, board_director, compliance, treasury, accounting,
//   security_admin, csr, csr_supervisor
//
// External Contractors:
//   contract_auditor, cpa_auditor, custodian
// =============================================================================

export const ROLES = [
  // Internal - Operations
  'csr',
  'csr_supervisor',
  // Internal - Administration
  'compliance',
  'accounting',
  'treasury',
  'security_admin',
  // Internal - Governance
  'council',
  'board_director',
  'admin',
  // External - Contractors
  'contract_auditor',
  'cpa_auditor',
  'custodian',
] as const;

export type Role = typeof ROLES[number];

export function isValidRole(role: string): role is Role {
  return ROLES.includes(role as Role);
}

// =============================================================================
// ROLE METADATA
// =============================================================================

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  csr: 'Customer service agent',
  csr_supervisor: 'CSR supervisor with escalation authority',
  compliance: 'Regulatory compliance, KYC/AML review',
  accounting: 'Transaction recording, financial reporting',
  treasury: 'Reserve management, mint/redeem authorization',
  security_admin: 'Security administration, pen test oversight',
  council: 'Council of Eight — governance, voting, veto',
  board_director: 'Board of Directors — oversight, intl relations',
  admin: 'Full system administration',
  contract_auditor: 'External — quarterly audits, smart contract review',
  cpa_auditor: 'External — monthly attestation, PCAOB reserve verification',
  custodian: 'External — third-party asset custody',
};

export const ROLE_TYPE: Record<Role, 'internal' | 'external'> = {
  csr: 'internal',
  csr_supervisor: 'internal',
  compliance: 'internal',
  accounting: 'internal',
  treasury: 'internal',
  security_admin: 'internal',
  council: 'internal',
  board_director: 'internal',
  admin: 'internal',
  contract_auditor: 'external',
  cpa_auditor: 'external',
  custodian: 'external',
};

export const ROLE_APP_ACCESS: Record<Role, string[]> = {
  csr: ['csr'],
  csr_supervisor: ['csr', 'admin-ui'],
  compliance: ['csr', 'admin-ui'],
  accounting: ['admin-ui'],
  treasury: ['admin-ui'],
  security_admin: ['csr', 'admin-ui'],
  council: ['csr', 'admin-ui'],
  board_director: ['csr', 'admin-ui'],
  admin: ['csr', 'admin-ui'],
  contract_auditor: ['admin-ui'],
  cpa_auditor: ['admin-ui'],
  custodian: ['admin-ui'],
};

// =============================================================================
// PERMISSIONS
// =============================================================================

export type Permission =
  // System
  | 'system:manage_users'
  | 'system:assign_roles'
  | 'system:configure'
  | 'system:view_audit_logs'
  | 'system:export_audit_logs'
  // Governance
  | 'governance:vote'
  | 'governance:veto'
  | 'governance:approve_policy'
  | 'governance:upgrade_contracts'
  | 'governance:view_proposals'
  // Reserve Management
  | 'reserve:authorize_mint'
  | 'reserve:authorize_redeem'
  | 'reserve:view_balances'
  | 'reserve:hold_release_assets'
  | 'reserve:transfer_funds'
  | 'reserve:view_ratio'
  | 'reserve:confirm_holdings'
  // Compliance & Audit
  | 'compliance:kyc_aml_review'
  | 'compliance:regulatory_reporting'
  | 'compliance:suspend_account'
  | 'audit:monthly_attestation'
  | 'audit:quarterly_audit'
  | 'audit:annual_security'
  | 'audit:flag_violations'
  | 'audit:view_reports'
  // Financial Records
  | 'finance:record_transactions'
  | 'finance:generate_reports'
  | 'finance:view_fee_distribution'
  | 'finance:modify_records'
  | 'finance:view_transaction_history'
  // Customer Operations
  | 'customer:view_data'
  | 'customer:process_requests'
  | 'customer:escalate'
  | 'customer:override_csr'
  | 'customer:authorize_refund'
  | 'customer:suspend_account'
  // Dashboard Widgets
  | 'dashboard:view_system_status'
  | 'dashboard:view_transactions'
  | 'dashboard:view_volume'
  | 'dashboard:view_fees'
  | 'dashboard:view_reserve'
  | 'dashboard:view_performance'
  | 'dashboard:view_anchors'
  | 'dashboard:view_governance'
  | 'dashboard:view_mint_requests'
  | 'dashboard:view_kyc_queue'
  | 'dashboard:view_aml_alerts'
  | 'dashboard:view_security_alerts'
  | 'dashboard:view_vulnerabilities'
  | 'dashboard:view_attestation'
  | 'dashboard:view_audit_reports'
  | 'dashboard:view_asset_holdings'
  // Legacy CSR permissions
  | 'csr:view_queue'
  | 'csr:claim'
  | 'csr:approve'
  | 'csr:reject'
  | 'csr:override'
  | 'csr:approve_override'
  | 'csr:assign'
  | 'csr:view_audit'
  | 'csr:export_audit'
  | 'csr:manage_templates'
  // Legacy Admin permissions
  | 'admin:view_dashboard'
  | 'admin:view_orders'
  | 'admin:cancel_order'
  | 'admin:retry_order'
  // Contracts
  | 'contracts:view'
  | 'contracts:review'
  // Threats / ngrokctl (7 permissions)
  | 'threats:view_status'
  | 'threats:view_blocked'
  | 'threats:scan'
  | 'threats:block'
  | 'threats:unblock'
  | 'threats:configure'
  | 'threats:master_switch';

// =============================================================================
// ROLE -> PERMISSIONS MAPPING
// =============================================================================

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  csr: [
    'customer:view_data',
    'customer:process_requests',
    'customer:escalate',
    'csr:view_queue',
    'csr:claim',
    'csr:approve',
    'csr:reject',
    'csr:override',
  ],

  csr_supervisor: [
    'customer:view_data',
    'customer:process_requests',
    'customer:escalate',
    'customer:override_csr',
    'customer:authorize_refund',
    'customer:suspend_account',
    'finance:view_transaction_history',
    'csr:view_queue',
    'csr:claim',
    'csr:approve',
    'csr:reject',
    'csr:override',
    'csr:approve_override',
    'csr:assign',
    'csr:view_audit',
    // Dashboard widgets (admin-ui)
    'dashboard:view_system_status',
    'dashboard:view_kyc_queue',
    'dashboard:view_aml_alerts',
  ],

  compliance: [
    'compliance:kyc_aml_review',
    'compliance:regulatory_reporting',
    'compliance:suspend_account',
    'audit:flag_violations',
    'audit:view_reports',
    'finance:generate_reports',
    'finance:view_fee_distribution',
    'finance:view_transaction_history',
    'reserve:view_balances',
    'reserve:view_ratio',
    'customer:view_data',
    'system:view_audit_logs',
    'system:export_audit_logs',
    'dashboard:view_system_status',
    'dashboard:view_kyc_queue',
    'dashboard:view_aml_alerts',
    'dashboard:view_reserve',
    'dashboard:view_audit_reports',
    'csr:view_queue',
    'csr:view_audit',
    'csr:export_audit',
    // Threats (read-only)
    'threats:view_status',
    'threats:view_blocked',
    // Contract rules (read-only)
    'contracts:view',
  ],

  accounting: [
    'finance:record_transactions',
    'finance:generate_reports',
    'finance:view_fee_distribution',
    'finance:modify_records',
    'finance:view_transaction_history',
    'compliance:regulatory_reporting',
    'reserve:view_balances',
    'reserve:view_ratio',
    'dashboard:view_system_status',
    'dashboard:view_transactions',
    'dashboard:view_volume',
    'dashboard:view_fees',
    'dashboard:view_reserve',
  ],

  treasury: [
    'reserve:authorize_mint',
    'reserve:authorize_redeem',
    'reserve:view_balances',
    'reserve:transfer_funds',
    'reserve:view_ratio',
    'finance:generate_reports',
    'finance:view_fee_distribution',
    'finance:view_transaction_history',
    'dashboard:view_system_status',
    'dashboard:view_reserve',
    'dashboard:view_mint_requests',
    'dashboard:view_transactions',
    'dashboard:view_volume',
    'dashboard:view_fees',
    'dashboard:view_anchors',
  ],

  security_admin: [
    'system:manage_users',
    'system:configure',
    'system:view_audit_logs',
    'system:export_audit_logs',
    'audit:annual_security',
    'audit:flag_violations',
    'customer:suspend_account',
    'dashboard:view_system_status',
    'dashboard:view_performance',
    'dashboard:view_security_alerts',
    'dashboard:view_vulnerabilities',
    // CSR (read-only — incident investigation)
    'csr:view_audit',
    'csr:view_queue',
    // Threats (read + mutation, NOT master_switch)
    'threats:view_status',
    'threats:view_blocked',
    'threats:scan',
    'threats:block',
    'threats:unblock',
    'threats:configure',
  ],

  council: [
    'governance:vote',
    'governance:veto',
    'governance:approve_policy',
    'governance:upgrade_contracts',
    'governance:view_proposals',
    'reserve:view_balances',
    'reserve:view_ratio',
    'finance:view_fee_distribution',
    'finance:view_transaction_history',
    'system:view_audit_logs',
    'dashboard:view_system_status',
    'dashboard:view_reserve',
    'dashboard:view_transactions',
    'dashboard:view_volume',
    'dashboard:view_fees',
    'dashboard:view_governance',
    'dashboard:view_anchors',
    'dashboard:view_audit_reports',
    // Contract rules (view rules they vote to upgrade)
    'contracts:view',
    // CSR (read-only — governance oversight)
    'csr:view_queue',
    'csr:view_audit',
  ],

  board_director: [
    'governance:vote',
    'governance:approve_policy',
    'governance:view_proposals',
    'reserve:view_balances',
    'reserve:view_ratio',
    'finance:view_fee_distribution',
    'finance:view_transaction_history',
    'audit:view_reports',
    'system:view_audit_logs',
    'dashboard:view_system_status',
    'dashboard:view_reserve',
    'dashboard:view_transactions',
    'dashboard:view_volume',
    'dashboard:view_fees',
    'dashboard:view_governance',
    'dashboard:view_audit_reports',
    // Contract rules (oversight)
    'contracts:view',
    // CSR (read-only — governance oversight)
    'csr:view_queue',
    'csr:view_audit',
  ],

  admin: [
    'system:manage_users',
    'system:assign_roles',
    'system:configure',
    'system:view_audit_logs',
    'system:export_audit_logs',
    'governance:vote',
    'governance:approve_policy',
    'governance:view_proposals',
    'reserve:view_balances',
    'reserve:view_ratio',
    'compliance:kyc_aml_review',
    'compliance:regulatory_reporting',
    'compliance:suspend_account',
    'audit:flag_violations',
    'audit:view_reports',
    'finance:generate_reports',
    'finance:view_fee_distribution',
    'finance:view_transaction_history',
    'customer:view_data',
    'customer:process_requests',
    'customer:escalate',
    'customer:override_csr',
    'customer:authorize_refund',
    'customer:suspend_account',
    'dashboard:view_system_status',
    'dashboard:view_transactions',
    'dashboard:view_volume',
    'dashboard:view_fees',
    'dashboard:view_reserve',
    'dashboard:view_performance',
    'dashboard:view_anchors',
    'dashboard:view_governance',
    'dashboard:view_mint_requests',
    'dashboard:view_kyc_queue',
    'dashboard:view_aml_alerts',
    'dashboard:view_security_alerts',
    'dashboard:view_vulnerabilities',
    'dashboard:view_attestation',
    'dashboard:view_audit_reports',
    'csr:view_queue',
    'csr:claim',
    'csr:approve',
    'csr:reject',
    'csr:override',
    'csr:approve_override',
    'csr:assign',
    'csr:view_audit',
    'csr:export_audit',
    'csr:manage_templates',
    'admin:view_dashboard',
    'admin:view_orders',
    'admin:cancel_order',
    'admin:retry_order',
    'contracts:view',
    // All Threats (including master_switch — admin only)
    'threats:view_status',
    'threats:view_blocked',
    'threats:scan',
    'threats:block',
    'threats:unblock',
    'threats:configure',
    'threats:master_switch',
  ],

  contract_auditor: [
    'contracts:view',
    'contracts:review',
    'audit:quarterly_audit',
    'audit:flag_violations',
    'audit:view_reports',
    'compliance:kyc_aml_review',
    'system:view_audit_logs',
    'system:export_audit_logs',
    'dashboard:view_system_status',
    'dashboard:view_anchors',
    'dashboard:view_performance',
    'dashboard:view_audit_reports',
  ],

  cpa_auditor: [
    'audit:monthly_attestation',
    'audit:quarterly_audit',
    'audit:flag_violations',
    'audit:view_reports',
    'reserve:view_balances',
    'reserve:view_ratio',
    'reserve:confirm_holdings',
    'finance:generate_reports',
    'finance:view_fee_distribution',
    'finance:view_transaction_history',
    'system:view_audit_logs',
    'system:export_audit_logs',
    'dashboard:view_reserve',
    'dashboard:view_fees',
    'dashboard:view_attestation',
    'dashboard:view_audit_reports',
    'dashboard:view_asset_holdings',
    // Contract rules (fee/reserve/dividend attestation)
    'contracts:view',
  ],

  custodian: [
    'reserve:view_balances',
    'reserve:view_ratio',
    'reserve:hold_release_assets',
    'reserve:confirm_holdings',
    'audit:flag_violations',
    'dashboard:view_reserve',
    'dashboard:view_asset_holdings',
  ],
};

// =============================================================================
// SEGREGATION OF DUTIES
// =============================================================================

export const PROHIBITED_ROLE_COMBINATIONS: [Role, Role, string][] = [
  ['treasury', 'custodian', 'Cannot authorize and hold same assets'],
  ['treasury', 'accounting', 'Cannot authorize and record same transactions'],
  ['compliance', 'contract_auditor', 'Cannot review and audit own compliance'],
  ['cpa_auditor', 'accounting', 'Cannot attest records you created'],
  ['custodian', 'accounting', 'Cannot hold assets and record transactions'],
  ['admin', 'council', 'System control + governance is excessive privilege'],
];

export function checkSegregationViolation(roles: Role[]): string | null {
  for (const [roleA, roleB, reason] of PROHIBITED_ROLE_COMBINATIONS) {
    if (roles.includes(roleA) && roles.includes(roleB)) {
      return `Segregation violation: ${roleA} + ${roleB} — ${reason}`;
    }
  }
  return null;
}

export function validateRoles(roles: Role[]): { valid: boolean; error?: string } {
  if (roles.length === 0) {
    return { valid: false, error: 'At least one role is required' };
  }
  for (const role of roles) {
    if (!isValidRole(role)) {
      return { valid: false, error: `Invalid role: ${role}` };
    }
  }
  const violation = checkSegregationViolation(roles);
  if (violation) {
    return { valid: false, error: violation };
  }
  return { valid: true };
}

// =============================================================================
// PERMISSION CHECKS
// =============================================================================

export function hasPermission(roles: Role[], permission: Permission): boolean {
  return roles.some(role => ROLE_PERMISSIONS[role]?.includes(permission));
}

export function getPermissions(roles: Role[]): Permission[] {
  const perms = new Set<Permission>();
  for (const role of roles) {
    for (const p of ROLE_PERMISSIONS[role] || []) {
      perms.add(p);
    }
  }
  return Array.from(perms);
}

export function canAccessApp(roles: Role[], app: 'csr' | 'admin-ui'): boolean {
  return roles.some(role => ROLE_APP_ACCESS[role]?.includes(app));
}

// =============================================================================
// EMPLOYEE TYPE
// =============================================================================

export interface Employee {
  id: string;
  email: string;
  name: string;
  roles: Role[];
  status: 'active' | 'inactive' | 'terminated';
}

export function employeeCan(employee: Employee, permission: Permission): boolean {
  return hasPermission(employee.roles, permission);
}

// =============================================================================
// PRIMARY ROLE
// =============================================================================

const ROLE_PRIORITY: Role[] = [
  'admin',
  'council',
  'board_director',
  'treasury',
  'compliance',
  'security_admin',
  'accounting',
  'cpa_auditor',
  'contract_auditor',
  'custodian',
  'csr_supervisor',
  'csr',
];

export function getPrimaryRole(roles: Role[]): Role {
  for (const role of ROLE_PRIORITY) {
    if (roles.includes(role)) {
      return role;
    }
  }
  return roles[0] || 'csr';
}

export function isExternalRole(role: Role): boolean {
  return ROLE_TYPE[role] === 'external';
}

export function hasExternalRole(roles: Role[]): boolean {
  return roles.some(role => isExternalRole(role));
}

